<template>
  <div :class="['app-banner', colorClass, sizeClass]">
    <div class="app-banner__image">
      <div
        v-if="ripple"
        class="banner-image-effects"
      />
      <slot name="image">
        <sion-banner-logo />
      </slot>
    </div>
    <div class="app-banner__content">
      <div
        v-if="$slots.heading || heading"
        class="app-banner__heading"
        data-cy="banner-heading"
      >
        <slot name="heading">{{ heading }}</slot>
      </div>
      <div
        v-if="$slots.subHeading || subHeading"
        class="app-banner__subheading"
        data-cy="banner-subheading"
      >
        <slot name="subHeading">{{ subHeading }}</slot>
      </div>
    </div>
    <div
      v-if="$slots.actions"
      class="app-banner__actions"
      data-cy="banner-actions"
    >
      <slot name="actions" />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import SionBannerLogo from '@/components/logos/SionBannerLogo.vue';

defineOptions({
  name: 'AppBanner',
});

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: (v) => ['sm', 'md', 'lg'].includes(v),
  },
  color: {
    type: String,
    default: 'light',
    validator: (v) => ['light', 'dark'].includes(v),
  },
  heading: {
    type: String,
    default: '',
  },
  subHeading: {
    type: String,
    default: '',
  },
  ripple: {
    type: Boolean,
    default: false,
  },
});

const colorClass = computed(() => {
  return {
    light: 'is-light',
    dark: 'is-dark',
  }[props.color];
});

const sizeClass = computed(() => {
  return {
    sm: 'is-sm',
    md: 'is-md',
    lg: 'is-lg',
  }[props.size];
});
</script>

<style>
@reference '@/assets/main.css';

.app-banner {
  @apply flex bg-[var(--banner-bg)] border-[var(--banner-border)] items-center shadow-xs overflow-hidden;

  &.is-light {
    --banner-bg: var(--color-gray-100);
    --banner-border: var(--color-gray-50);
    --banner-effect-border: var(--color-gray-200);

    & .app-banner__content {
      @apply text-gray-700;
    }
  }

  &.is-dark {
    --banner-bg: var(--color-gray-cool-900);
    --banner-border: var(--color-gray-300);
    --banner-effect-border: var(--color-gray-900);

    & .app-banner__content {
      @apply text-white;
    }
  }

  &.is-sm {
    @apply p-3 pl-5 rounded-lg gap-4 border-2;

    & .app-banner__image {
      @apply w-6 h-6;
    }

    & .app-banner__heading {
      @apply text-xs;
    }

    & .app-banner__subheading {
      @apply text-xs;
    }
  }

  &.is-md {
    @apply p-4 pl-6 rounded-xl gap-4 border-4;

    & .app-banner__image {
      @apply w-8 h-8;
    }

    & .app-banner__heading {
      @apply text-sm;
    }

    & .app-banner__subheading {
      @apply text-xs;
    }
  }

  &.is-lg {
    @apply p-8 pl-6 rounded-2xl gap-6 border-4;

    & .app-banner__image {
      @apply w-12 h-12;
    }

    & .app-banner__heading {
      @apply text-base;
    }

    & .app-banner__subheading {
      @apply text-sm;
    }
  }
}

.app-banner__image {
  @apply relative;

  & .banner-image-effects {
    @apply absolute rounded-full top-[-17%] left-[-17%] w-[134%] h-[134%];
    background: linear-gradient(250deg, rgba(97, 114, 243, 0.08) 9%, rgba(0, 168, 159, 0.08) 89%);
    box-shadow:
      0 0 0 10px var(--banner-bg),
      0 0 0 11px var(--banner-effect-border),
      0 0 0 25px var(--banner-bg),
      0 0 0 26px var(--banner-effect-border),
      0 0 0 40px var(--banner-bg),
      0 0 0 41px var(--banner-effect-border);
  }

  & svg {
    @apply w-full h-full;
  }
}

.app-banner__content {
  @apply relative flex flex-col gap-1;
}

.app-banner__heading {
  @apply inline-flex items-center gap-3 font-semibold;
}

.app-banner__subheading {
  @apply text-xs;
}

.app-banner__actions {
  @apply flex gap-3 ml-auto min-w-fit;
}
</style>
